import { useState, useCallback, useEffect } from "react";
import { TableRow } from "../../types/types.table";
import { PendingRow } from "./usePendingRows";

interface UseTableSelectionProps {
  data: TableRow[];
  pendingRows: PendingRow[];
  visibleColumns?: Array<{ name: string }>; 
}

export function useTableSelection(props: UseTableSelectionProps) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);
  const [selectedRowType, setSelectedRowType] = useState<"data" | "pending">("data");
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number>(0); 

  const allRows = [...props.data, ...props.pendingRows];

  useEffect(() => {
    if (props.data.length === 0 && props.pendingRows.length > 0) {
      setSelectedRowType("pending");
      setSelectedRowIndex(0);
    } else if (props.data.length > 0 && selectedRowIndex === 0 && selectedRowType === "pending") {
      setSelectedRowType("data");
    }
  }, [props.data.length, props.pendingRows.length]);

  const getSelectedRow = useCallback(() => {
    if (selectedRowType === "data") {
      return props.data[selectedRowIndex];
    } else {
      const pendingIndex = selectedRowIndex - props.data.length;
      return props.pendingRows[pendingIndex];
    }
  }, [selectedRowIndex, selectedRowType, props.data, props.pendingRows]);

  const moveSelection = useCallback((direction: number) => {
    const totalRows = allRows.length;
    if (totalRows === 0) return;

    let newIndex = selectedRowIndex + direction;

    if (newIndex < 0) newIndex = totalRows - 1;
    if (newIndex >= totalRows) newIndex = 0;

    setSelectedRowIndex(newIndex);

    if (newIndex < props.data.length) {
      setSelectedRowType("data");
    } else {
      setSelectedRowType("pending");
    }
  }, [selectedRowIndex, allRows.length, props.data.length]);

  const moveColumnSelection = useCallback((direction: number) => {
    if (!props.visibleColumns || props.visibleColumns.length === 0) return;

    let newColumnIndex = selectedColumnIndex + direction;
    
    if (newColumnIndex < 0) {
      newColumnIndex = 0;
    } else if (newColumnIndex >= props.visibleColumns.length) {
      newColumnIndex = props.visibleColumns.length - 1;
    }
    
    if (newColumnIndex === selectedColumnIndex) {
      return;
    }

    setSelectedColumnIndex(newColumnIndex);

    requestAnimationFrame(() => {
      queueMicrotask(() => {
        const selectedColumn = props.visibleColumns![newColumnIndex];
        if (!selectedColumn) return;
        
        const rowElement = document.querySelector(
          `[data-row-index="${selectedRowIndex}"][data-row-type="${selectedRowType}"]`
        );
        
        if (!rowElement) return;
        
        const cellElement = rowElement.querySelector(
          `[data-field-name="${selectedColumn.name}"]`
        ) as HTMLElement;
        
        if (!cellElement) return;
        
        const selectors = [
          'input:not([type="hidden"]):not([disabled])',
          'textarea:not([disabled])',
          'select:not([disabled])',
          'button:not([disabled])',
          '[role="combobox"]:not([disabled])',
          '[contenteditable="true"]',
        ];
        
        let focused = false;
        
        for (const selector of selectors) {
          const focusable = cellElement.querySelector(selector) as HTMLElement;
          if (focusable && focusable.offsetParent !== null) {
            focusable.focus();
            
            if (focusable instanceof HTMLInputElement) {
              const inputType = focusable.type;
              if (['text', 'number', 'email', 'tel', 'url', 'search', 'password'].includes(inputType)) {
                setTimeout(() => {
                  try {
                    focusable.select();
                  } catch (e) {
                    // Ignore
                  }
                }, 0);
              }
            } else if (focusable instanceof HTMLTextAreaElement) {
              setTimeout(() => {
                try {
                  focusable.select();
                } catch (e) {
                  // Ignore
                }
              }, 0);
            }
            
            focused = true;
            break;
          }
        }
        
        if (!focused && cellElement.offsetParent !== null) {
          cellElement.setAttribute('tabindex', '0');
          cellElement.focus();
        }
      });
    });
  }, [selectedColumnIndex, props.visibleColumns, selectedRowIndex, selectedRowType]);

  const selectFirst = useCallback(() => {
    if (allRows.length > 0) {
      setSelectedRowIndex(0);
      setSelectedRowType(props.data.length > 0 ? "data" : "pending");
    }
  }, [allRows.length, props.data.length]);

  const selectLast = useCallback(() => {
    if (allRows.length > 0) {
      const lastIndex = allRows.length - 1;
      setSelectedRowIndex(lastIndex);
      setSelectedRowType(lastIndex < props.data.length ? "data" : "pending");
    }
  }, [allRows.length, props.data.length]);

  const focusFirstColumn = useCallback(() => {
    setSelectedColumnIndex(0);
    
    const currentFocused = document.activeElement as HTMLElement;
    if (currentFocused && currentFocused !== document.body) {
      currentFocused.blur();
    }
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!props.visibleColumns || props.visibleColumns.length === 0) return;
        
        const firstColumn = props.visibleColumns[0];
        if (!firstColumn) return;
        
        const rowElement = document.querySelector(
          `[data-row-index="${selectedRowIndex}"][data-row-type="${selectedRowType}"]`
        );
        
        if (!rowElement) return;
        
        const cellElement = rowElement.querySelector(
          `[data-field-name="${firstColumn.name}"]`
        ) as HTMLElement;
        
        if (!cellElement) return;
        
        const selectors = [
          'input:not([type="hidden"]):not([disabled])',
          'textarea:not([disabled])',
          'select:not([disabled])',
          'button:not([disabled])',
          '[role="combobox"]:not([disabled])',
          '[contenteditable="true"]',
        ];
        
        for (const selector of selectors) {
          const focusable = cellElement.querySelector(selector) as HTMLElement;
          if (focusable && focusable.offsetParent !== null) {
            focusable.focus();
            
            if (focusable instanceof HTMLInputElement) {
              const inputType = focusable.type;
              if (['text', 'number', 'email', 'tel', 'url', 'search', 'password'].includes(inputType)) {
                setTimeout(() => {
                  try {
                    focusable.select();
                  } catch (e) {
                    // Ignore
                  }
                }, 0);
              }
            } else if (focusable instanceof HTMLTextAreaElement) {
              setTimeout(() => {
                try {
                  focusable.select();
                } catch (e) {
                  // Ignore
                }
              }, 0);
            }
            
            break;
          }
        }
      });
    });
  }, [props.visibleColumns, selectedRowIndex, selectedRowType]);

  return {
    selectedRowIndex,
    selectedRowType,
    selectedColumnIndex, 
    selectedRow: getSelectedRow(),
    moveSelection,
    moveColumnSelection,
    selectFirst,
    selectLast,
    setSelectedRowIndex,
    setSelectedRowType,
    setSelectedColumnIndex, 
    focusFirstColumn,
  };
}